<template>
    <div class="action-plan-container">
        <b-overlay :show="loading" rounded="sm">
            <template v-if="actionPlans && actionPlans.pillars && actionPlans.pillars.length > 0">
                <!-- Progress con prioridades -->
                <action-plan-priority-progress :summary="actionPlans.summary"
                    :priority-definitions="priorityDefinitions" class="mb-4" />

                <!-- Filtros -->
                <action-plan-filters v-model="filters" :pillars="actionPlans.pillars"
                    :status-definitions="statusDefinitions" :priority-definitions="priorityDefinitions" class="mb-4" />

                <!-- Layout de 2 columnas -->
                <div class="action-plan-layout">
                    <!-- Lista de ítems (izquierda) -->
                    <div class="items-list-column">
                        <action-plan-items-list :pillars="filteredPillars"
                            :selected-item-id="selectedItem && selectedItem.id" :status-definitions="statusDefinitions"
                            @item-selected="handleItemSelected">
                            <template #header-actions>
                                <button v-if="hasChangesToNotify" class="btn-notify-small"
                                    @click="openNotificationModal" title="Notificar cambios de responsable">
                                    <i class="fa fa-envelope"></i>
                                    <span>Notificar cambios ({{ uniqueResponsiblesCount }})</span>
                                </button>
                            </template>
                        </action-plan-items-list>
                    </div>

                    <!-- Panel de detalle (derecha) -->
                    <div class="detail-panel-column">
                        <action-plan-detail-panel :item="selectedItem" :pillar-name="selectedItemPillarName"
                            :element-name="selectedItemElementName" :company-id="companyId" :workers="workers"
                            :status-definitions="statusDefinitions" :priority-definitions="priorityDefinitions"
                            :notification-frequency-definitions="notificationFrequencyDefinitions"
                            @updated="handleItemUpdated" @worker-created="handleWorkerCreated"
                            @responsible-changed="handleResponsibleChanged" />
                    </div>
                </div>

                <!-- Modal de confirmación de notificaciones -->
                <action-plan-notification-modal :changes="changedResponsibles" :workers="workers"
                    @confirm="confirmSendNotifications" />
            </template>

            <template v-else-if="!loading">
                <b-alert show variant="info" class="text-center">
                    <i class="fa fa-info-circle mr-2"></i>
                    No hay planes de acción para este diagnóstico
                </b-alert>
            </template>
        </b-overlay>
    </div>
</template>

<script>
import ActionPlanFilters from '../actionPlan/ActionPlanFilters.vue';
import ActionPlanItemsList from '../actionPlan/ActionPlanItemsList.vue';
import ActionPlanDetailPanel from '../actionPlan/ActionPlanDetailPanel.vue';
import ActionPlanPriorityProgress from '../actionPlan/ActionPlanPriorityProgress.vue';
import ActionPlanNotificationModal from '../actionPlan/ActionPlanNotificationModal.vue';
import Pusher from 'pusher-js';
import { mapActions } from 'vuex';

export default {
    name: "ActionPlanView",
    components: {
        ActionPlanFilters,
        ActionPlanItemsList,
        ActionPlanDetailPanel,
        ActionPlanPriorityProgress,
        ActionPlanNotificationModal
    },
    props: {
        actionPlans: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        companyId: {
            type: Number,
            default: null
        },
        workers: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            filters: {
                pillarId: null,
                elementId: null,
                status: null,
                priority: null,
                search: ""
            },
            selectedItem: null,
            selectedItemPillarName: '',
            selectedItemElementName: '',
            changedResponsibles: [],
            pusherChannel: null
        };
    },
    computed: {
        statusDefinitions() {
            return this.actionPlans && this.actionPlans.status_definitions
                ? this.actionPlans.status_definitions
                : [];
        },
        priorityDefinitions() {
            return this.actionPlans && this.actionPlans.priority_definitions
                ? this.actionPlans.priority_definitions
                : [
                    { value: 'Alto', label: 'Alto', color: '#DC2626', bg_color: '#FEE2E2', text_color: '#991B1B', icon: 'fa-arrow-up' },
                    { value: 'Medio', label: 'Medio', color: '#F59E0B', bg_color: '#FEF3C7', text_color: '#92400E', icon: 'fa-minus' },
                    { value: 'Bajo', label: 'Bajo', color: '#22C55E', bg_color: '#D1FAE5', text_color: '#065F46', icon: 'fa-arrow-down' }
                ];
        },
        filteredPillars() {
            if (!this.actionPlans || !this.actionPlans.pillars) return [];

            let pillars = JSON.parse(JSON.stringify(this.actionPlans.pillars));

            // Filtrar por pilar
            if (this.filters.pillarId) {
                pillars = pillars.filter(p => p.id === this.filters.pillarId);
            }

            // Filtrar por elemento
            if (this.filters.elementId) {
                pillars = pillars.map(pillar => ({
                    ...pillar,
                    elements: pillar.elements.filter(e => e.id === this.filters.elementId)
                })).filter(p => p.elements.length > 0);
            }

            // Filtrar por estado
            if (this.filters.status) {
                pillars = this.filterByStatus(pillars, this.filters.status);
            }

            // Filtrar por prioridad
            if (this.filters.priority) {
                pillars = this.filterByPriority(pillars, this.filters.priority);
            }

            // Filtrar por búsqueda
            if (this.filters.search) {
                pillars = this.filterBySearch(pillars, this.filters.search);
            }

            return pillars;
        },
        notificationFrequencyDefinitions() {
            return this.actionPlans && this.actionPlans.notification_frequency_definitions
                ? this.actionPlans.notification_frequency_definitions
                : [];
        },
        hasChangesToNotify() {
            return this.changedResponsibles.length > 0;
        },
        uniqueResponsiblesCount() {
            const uniqueIds = new Set(this.changedResponsibles.map(c => c.responsibleId));
            return uniqueIds.size;
        }
    },
    methods: {
        filterByStatus(pillars, status) {
            return pillars.map(pillar => ({
                ...pillar,
                elements: pillar.elements.map(element => ({
                    ...element,
                    general_items: element.general_items.map(generalItem => ({
                        ...generalItem,
                        items: generalItem.items.filter(item => {
                            return item.plan_action && item.plan_action.status === status;
                        })
                    })).filter(gi => gi.items.length > 0)
                })).filter(e => e.general_items.length > 0)
            })).filter(p => p.elements.length > 0);
        },
        filterByPriority(pillars, priority) {
            return pillars.map(pillar => ({
                ...pillar,
                elements: pillar.elements.map(element => ({
                    ...element,
                    general_items: element.general_items.map(generalItem => ({
                        ...generalItem,
                        items: generalItem.items.filter(item => {
                            return item.plan_action && item.plan_action.priority === priority;
                        })
                    })).filter(gi => gi.items.length > 0)
                })).filter(e => e.general_items.length > 0)
            })).filter(p => p.elements.length > 0);
        },
        filterBySearch(pillars, search) {
            const searchLower = search.toLowerCase();
            return pillars.map(pillar => ({
                ...pillar,
                elements: pillar.elements.map(element => ({
                    ...element,
                    general_items: element.general_items.map(generalItem => ({
                        ...generalItem,
                        items: generalItem.items.filter(item =>
                            item.name.toLowerCase().includes(searchLower) ||
                            (item.plan_action && item.plan_action.corrective_action && item.plan_action.corrective_action.toLowerCase().includes(searchLower))
                        )
                    })).filter(gi => gi.items.length > 0)
                })).filter(e => e.general_items.length > 0)
            })).filter(p => p.elements.length > 0);
        },
        handleItemSelected({ item, pillarName, elementName }) {
            this.selectedItem = item;
            this.selectedItemPillarName = pillarName;
            this.selectedItemElementName = elementName;
        },
        handleItemUpdated() {
            this.$emit('item-updated');
        },
        handleWorkerCreated() {
            this.$emit('worker-created');
        },
        handleResponsibleChanged(data) {
            // data includes: planActionId, planActionName, correctiveAction, responsibleId
            const index = this.changedResponsibles.findIndex(c => c.planActionId === data.planActionId);
            if (index !== -1) {
                // Actualizar info si ya existe
                this.$set(this.changedResponsibles, index, data);
            } else {
                this.changedResponsibles.push(data);
            }
        },
        openNotificationModal() {
            if (this.changedResponsibles.length === 0) return;
            this.$bvModal.show('modal-notification-confirm');
        },
        async confirmSendNotifications() {
            try {
                // Agrupar por responsable para el payload optimizado
                const grouped = {};
                this.changedResponsibles.forEach(c => {
                    if (!grouped[c.responsibleId]) {
                        grouped[c.responsibleId] = {
                            responsible_id: c.responsibleId,
                            plan_action_ids: []
                        };
                    }
                    grouped[c.responsibleId].plan_action_ids.push(c.planActionId);
                });

                const payload = {
                    company_id: this.companyId,
                    notifications: Object.values(grouped)
                };

                const response = await this.$store.dispatch('skudo/diagnostics/sendActionPlanNotifications', payload);

                if (response && response.summary && response.summary.message) {
                    this.$alertWithHtml(
                        '¡Notificaciones enviadas!',
                        response.summary.message,
                        null,   // Time null para que no se cierre auto si es largo
                        true,   // Boton confirmar
                        'success'
                    );
                } else {
                    this.$alertSuccess(`Notificaciones enviadas correctamente a ${this.uniqueResponsiblesCount} responsable(s).`);
                }

                this.$bvModal.hide('modal-notification-confirm');

                // Limpiar lista después de enviar
                this.changedResponsibles = [];
            } catch (error) {
                this.$alertError('Error al enviar las notificaciones. Por favor, intente nuevamente.');
            }
        },
        subscribeToPusher() {
            if (!this.companyId) {
                return;
            }

            Pusher.logToConsole = false;

            const pusher = new Pusher('5c41c1dc4bec329b2165', {
                cluster: 'us2'
            });

            const channelName = `company.${this.companyId}.action-plan-notifications`;
            this.pusherChannel = pusher.subscribe(channelName);

            this.pusherChannel.bind('action-plan-notifications-sent', (data) => {
                this.changedResponsibles = [];
            });
        },
        unsubscribePusher() {
            if (this.pusherChannel) {
                const channelName = `company.${this.companyId}.action-plan-notifications`;
                this.pusherChannel.unsubscribe(channelName);
                this.pusherChannel = null;
            }
        }
    },
    mounted() {
        this.subscribeToPusher();
    },
    beforeDestroy() {
        this.unsubscribePusher();
    },
    // Adding mounted hook to check if store action exists just in case? No, overkill.
};
</script>

<style scoped>
.action-plan-container {
    width: 100%;
}

/* Layout de 2 columnas */
.action-plan-layout {
    display: grid;
    grid-template-columns: 450px 1fr;
    gap: 24px;
    margin-top: 16px;
}

.items-list-column {
    position: sticky;
    top: 20px;
    align-self: start;
}

.detail-panel-column {
    min-width: 0;
}

.btn-notify-small {
    background-color: #5bc0de;
    border: none;
    color: white;
    font-size: 12px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-notify-small:hover {
    background-color: #31b0d5;
}

/* Responsive */
@media (max-width: 1024px) {
    .action-plan-layout {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .items-list-column {
        position: static;
    }
}
</style>
